import forge from "node-forge";
import * as pkijs from "pkijs";
import * as pvtsutils from "pvtsutils";
import * as asn1js from "asn1js";
import * as nodecrypto from "crypto";
import "reflect-metadata";
import * as x509 from "@peculiar/x509";
import { Crypto } from "@peculiar/webcrypto";
import { Logger } from "@/logger/logger";
import { 
  checkString, 
  checkPEM, 
  fromPEM, 
  toPEM, 
  formatPEM 
} from "@/utils/cryptoutils";
import { 
  Subject, 
  getDNInWenjaFormatFromString, 
  parseOIDinDN 
} from "@/pki/subject";
import {
  // ALLOWED_RDN_TYPES,
  getSanTypeById,
  getRdnTypeByOid,
  ALLOWED_SAN_TYPES,
} from "@/pki/nameTypes";
import NodeEngine from "../utils/NodeEngine";

const logger = new Logger("dfnpki");


// Crypto-Parameter für encrypt() und decrypt()
const ITERATIONS = 100100;
const ENCRYPTION_MODE = "AES-CBC";
const ENCRYPTION_ALGORITHM = "aes-128-cbc";
const KEY_LENGTH = 16;
const IV_LENGTH = 16;
const SALT_LENGTH = 128;

// Crypto-Parameter für createP12()
const P12_SALT_LENGTH = 16;
const P12_ITERATIONS = 599999;
const P12_ALGORITHM = "DES-EDE3-CBC";
const P12_HASH_ALGORITHM = "SHA-1";
const P12_PBE_SCHEMA = "PBES";

/**
 * Erzeugt den SHA1-Hash eines Strings.
 *
 * @param {String} input der zu hashende Text
 * @returns {String} Hexadezimaldarstellung des SHA1-Hashes
 */
export async function sha1(input) {
  const crypto = pkijs.getCrypto(true);
  checkString("input in sha1", input);

  const inputArray = pvtsutils.Convert.FromString(input);
  const hashBuffer = await crypto.digest("SHA-1", inputArray);
  const hexHash = pvtsutils.Convert.ToHex(hashBuffer);

  return hexHash;
}

/**
 * Kombiniert einen privaten Schlüssel und ein Zertifikat zu einem PKCS#12.
 * Schlüssel und Zertifikat müssen als PEM-String vorliegen. Die Ausgabe ist
 * ein Base64-String.
 * Das Zertifikat muss zusammen mit der Zertifikatskette als Array aus PEMs
 * übergeben werden.
 *
 * Der Zusatz {algorithm: '3des'} wird empfohlen, wenn man Probleme hat, das
 * resultierende P12 in den Browser zu importieren. Da das beim Testen der
 * Fall war, steht es hier mit drin.
 *
 * @param {String} privateKey privater Schlüssel im PEM-Format
 * @param {Array[String]} chain Zertifikat und Kette im PEM-Format
 * @param {String} password Passwort, mit dem die Ausgabe verschlüsselt wird
 * @returns {String} P12 als Base64-String
 */
export async function createP12(privateKey, chain, password, isForgeRequest) {
  checkPEM("privateKey in createP12", privateKey);
  checkString("password in createP12", password);

  const encodedChain = [];
  for (let cert of chain) {
    checkPEM("Ein Zertifikat in chain in createP12", cert);
    encodedChain.push(_parseCert(cert));
  }
  logger.debug("certs in chain geparsed");

  if (!encodedChain || !password) {
    logger.error("Ein Aufrufparameter von createP12() ist ungültig.");
    throw new Error("Ein Aufrufparameter von createP12() ist ungültig.");
  }

  // Friendly Name setzt sich zusammen aus CN des Certs und O des Issuers
  const certInfo = parseCert(chain[0]);
  const subjectCN = certInfo.subject.CN[0];
  var friendlyName = subjectCN + " issued by ";
  if (certInfo.issuer.O !== null && certInfo.issuer.O !== undefined) {
    friendlyName += certInfo.issuer.O[0];
  } else {
    friendlyName += certInfo.issuer;
  }
  logger.debug("Führe Key und Chain zusammen. friendlyName: " + friendlyName);

  if (isForgeRequest) {
    return legacyCreateP12(privateKey, chain, password, friendlyName)
  }

  const p12 = await _createP12(password, chain[0], chain.slice(1, chain.length), privateKey, friendlyName);
  //logger.debug("p12: \n" + formatPEM(p12));

  return formatPEM(p12);
}

function legacyCreateP12(privateKey, chain, password, friendlyName) {
  const encodedPrivateKey = forge.pki.privateKeyFromPem(privateKey);

  const encodedChain = [];
  for (let cert of chain) {
    checkPEM("Ein Zertifikat in chain in createP12", cert);
    encodedChain.push(forge.pki.certificateFromPem(cert));
  }

  if (!encodedPrivateKey || !encodedChain || !password) {
    logger.error("Ein Aufrufparameter von createP12() ist ungültig.");
    throw new Error("Ein Aufrufparameter von createP12() ist ungültig.");
  }

  var p12Asn1 = forge.pkcs12.toPkcs12Asn1(
    encodedPrivateKey,
    encodedChain,
    password,
    {
      friendlyName: friendlyName,
      saltSize: P12_SALT_LENGTH,
      count: P12_ITERATIONS,
      algorithm: 'aes256',
    }
  );

  // base64-encode p12
  var p12Der = forge.asn1.toDer(p12Asn1).getBytes();
  return forge.util.encode64(p12Der);
}

async function _createP12(password, subjectCert, chain, privateKey, friendlyName) {
  const prevEngine = pkijs.getEngine();
  const nodeEngine = new NodeEngine();
  pkijs.setEngine(nodeEngine.name, nodeEngine);

  //#region Initial variables
  const keyLocalIDBuffer = new ArrayBuffer(20);
  pkijs.getRandomValues(new Uint8Array(keyLocalIDBuffer));
  const passwordConverted = pvtsutils.Convert.FromString(password);

  //#region Create simplified structures for certificate and private key
  const certSimpl = pkijs.Certificate.fromBER(fromPEM(subjectCert));
  const pkcs8Simpl = toPrivateKeyInfo(privateKey, password);
  //#endregion

  const contentEncryptionAlgorithm = pkijs.getAlgorithmParameters(
    P12_ALGORITHM,
    "encrypt"
  ).algorithm;
  if ("name" in contentEncryptionAlgorithm === false)
    throw new Error(`No support for selected algorithm: ${P12_ALGORITHM}`);

  const makeInternalValuesParameters = {
    password: passwordConverted,
    contentEncryptionAlgorithm,
    hmacHashAlgorithm: P12_HASH_ALGORITHM,
    iterationCount: P12_ITERATIONS,
  };

  if (P12_PBE_SCHEMA) {
    makeInternalValuesParameters.pbeSchema = P12_PBE_SCHEMA;
  }
  logger.debug("InternalValuesParameters gesetzt");

  //#endregion

  //#region Put initial values for PKCS#12 structures
  const pkcs12 = new pkijs.PFX({
    parsedValue: {
      integrityMode: 0, // Password-Based Integrity Mode
      authenticatedSafe: new pkijs.AuthenticatedSafe({
        parsedValue: {
          safeContents: [
            {
              privacyMode: 0, // "No-privacy" Protection Mode
              value: new pkijs.SafeContents({
                safeBags: [
                  // SubjectCert
                  new pkijs.SafeBag({
                    bagId: "1.2.840.113549.1.12.10.1.3",
                    bagValue: new pkijs.CertBag({
                      parsedValue: certSimpl,
                    }),
                    bagAttributes: [
                      new pkijs.Attribute({
                        type: "1.2.840.113549.1.9.21", // localKeyID
                        values: [
                          new asn1js.OctetString({
                            valueHex: keyLocalIDBuffer,
                          }),
                        ],
                      }),
                      new pkijs.Attribute({
                        type: "1.2.840.113549.1.9.20", // friendlyName
                        values: [new asn1js.BmpString({ value: friendlyName })],
                      }),
                    ],
                  }),
                ],
              }),
            },
            {
              privacyMode: 0, // "No-privacy" Protection Mode
              value: new pkijs.SafeContents({
                safeBags: [
                  new pkijs.SafeBag({
                    bagId: "1.2.840.113549.1.12.10.1.2",
                    bagValue: new pkijs.PKCS8ShroudedKeyBag({
                      parsedValue: pkcs8Simpl,
                    }),
                    bagAttributes: [
                      new pkijs.Attribute({
                        type: "1.2.840.113549.1.9.21", // localKeyID
                        values: [
                          new asn1js.OctetString({
                            valueHex: keyLocalIDBuffer,
                          }),
                        ],
                      }),
                      new pkijs.Attribute({
                        type: "1.2.840.113549.1.9.20", // friendlyName
                        values: [new asn1js.BmpString({ value: friendlyName })],
                      }),
                    ],
                  }),
                ],
              }),
            },
          ],
        },
      }),
    },
  });
  //#endregion
  logger.debug("PFX erzeugt");

  //#region Create CertBags for all other Certs in the Chain
  for (let cert of chain) {
    const parsedCert = pkijs.Certificate.fromBER(fromPEM(cert));
    pkcs12.parsedValue.authenticatedSafe.parsedValue.safeContents[0].value.safeBags.push(
      new pkijs.SafeBag({
        bagId: "1.2.840.113549.1.12.10.1.3",
        bagValue: new pkijs.CertBag({
          parsedValue: parsedCert,
        })
      })
    )
  }
  //#endregion

  //#region Encode internal values for "PKCS8ShroudedKeyBag"
  pkijs.ParameterError.assertEmpty(pkcs12.parsedValue, "parsedValue", "pkcs12");
  pkijs.ParameterError.assertEmpty(
    pkcs12.parsedValue.authenticatedSafe,
    "authenticatedSafe",
    "pkcs12.parsedValue"
  );
  await pkcs12.parsedValue.authenticatedSafe.parsedValue.safeContents[1].value.safeBags[0].bagValue.makeInternalValues(
    makeInternalValuesParameters
  );
  //#endregion

  //#region Encode internal values for all "SafeContents" firts (create all "Privacy Protection" envelopes)
  if (!(pkcs12.parsedValue && pkcs12.parsedValue.authenticatedSafe)) {
    logger.error("pkcs12.parsedValue.authenticatedSafe is empty");
  }

  await pkcs12.parsedValue.authenticatedSafe.makeInternalValues({
    safeContents: [
      {
        // empty because "no privacy" protection mode
      },
      {
        // empty because "no privacy" protection mode
      },
    ],
  });
  //#endregion
  logger.debug("PFX internal values erzeugt, authenticated safe");

  //#region Encode internal values for "Integrity Protection" envelope
  await pkcs12.makeInternalValues({
    password: passwordConverted,
    iterations: P12_ITERATIONS,
    pbkdf2HashAlgorithm: P12_HASH_ALGORITHM,
    hmacHashAlgorithm: P12_HASH_ALGORITHM,
  });
  //#endregion
  logger.debug("PFX internal values erzeugt, integrity");

  const result = pvtsutils.Convert.ToBase64(pkcs12.toSchema().toBER(false));
  pkijs.setEngine(prevEngine);
  return result;
}

/**
 * Parst Altnames im Forge-Format zu unserem Format.
 *
 * Eigentlich ließe sich das auch per einfachem map() machen, aber Forge
 * speichert bei IP-Adressen und Microsoft-UPN den Wert leider nicht im value,
 * sondern an einer komplizierteren Stelle.
 *
 * @param {Array[Object]} forgeAltnames Altnames im Forge-Format
 * @returns {Array[Object]} Array mit Type-Value-Paaren
 */
function parseForgeAltnames(forgeAltnames) {
  const altnames = [];
  for (const altname of forgeAltnames) {
    var value;
    /*
    Sonderfall für IP-Adressen, S
    Struktur unterscheided sich leicht vom Standard:
    {
      "type": 7,
      "value": "À¨\u0001\u0001",
      "ip": "192.168.1.1"
    }
    */
    if (altname.type === 7) {
      value = altname.ip;
      /*
      Sonderfall für otherName bzw. Microsoft_UPN
      Komplexe Struktur die stark vom Standard abweicht:
      {
        "type": 0,
        "value": [
          {
            "tagClass": 0,
            "type": 6,
            "constructed": false,
            "composed": false,
            "value": "+\u0006\u0001\u0004\u0001�7\u0014\u0002\u0003"
          },
          {
            "tagClass": 128,
            "type": 0,
            "constructed": true,
            "composed": true,
            "value": [
              {
                "tagClass": 0,
                "type": 12,
                "constructed": false,
                "composed": false,
                "value": "TestUPN"
              }
            ]
          }
        ]
      }
      */
    } else if (altname.type === 0) {
      value = altname.value[1].value[0].value;
      /*
      Standardverfahren bei allen anderen SAN-Typen, Standardstruktur:
      {
        "type": 2,
        "value": "beispiel.com"
      }
      */
    } else {
      value = altname.value;
    }

    // Neuen SAN mit type und value erstellen und ins Array pushen.
    altnames.push({
      type: getSanTypeById(altname.type),
      value: value,
    });
  }
  return altnames;
}

function parseAltnames(altnames) {
  const wenjaAltnames = [];
  for (const item of altnames.items) {
    var type = item.type;
    const value = item.value;

    /** SAN Typen in PKIjs:
     * const DNS = "dns";
     * const DN = "dn";
     * const EMAIL = "email";
     * const IP = "ip";
     * const URL = "url";
     * const GUID = "guid";
     * const UPN = "upn";
     * const REGISTERED_ID = "id";
     * */ 
    switch (type) {
      case "email":
        break;
      case "upn":
        type = "Microsoft_UPN";
        break;
      case "url":
        type = "URI";
        break;
      case "ip":
        type = "IPAddress"
        break;
      case "dns":
        type = "DNS";
        break;
      default:
        break;
    }

    wenjaAltnames.push({
      type: ALLOWED_SAN_TYPES[type],
      value: value,
    });
  };

  return wenjaAltnames;
}

/**
 * Parst mit PKIjs einen PKCS10-Request und extrahiert alle von uns benötigten
 * Informationen.
 *
 * Beispielrückgabe:
 * {rdns: [{type: ALLOWED_RDN_TYPES.C, value: "DE"}],
 * altnames: []}
 *
 * @param {type} requestPEM der Antrag als PEM
 * @returns {array} Object mit Type-Value-Arrays rdns und altnames
 */
export function parsePKCS10(requestPEM) {
  logger.debug("parsePKCS10()");
  checkPEM("request in parsePKCS10", requestPEM);

  // pem to binary array
  const pkcs10 = pkijs.CertificationRequest.fromBER(fromPEM(requestPEM));
  //logger.debug("pkcs10:\n" + pkcs10);

  if (!pkcs10) {
    logger.error(
      "PKCS10-Antrag konnte nicht geparst werden. Eingabe: " + requestPEM
    );
    throw new Error("Konnte PKCS10-Antrag nicht parsen.");
  }

  // RDNs parsen
  var rdns = pkcs10.subject.typesAndValues.map((attribute) => ({
    type: getRdnTypeByOid(attribute.type),
    value: attribute.value.valueBlock.value,
  }));

  // SANs parsen
  var san = null;
  if (pkcs10.attributes != undefined) {
    var seq = pkcs10.attributes.find((o) => o.type === "1.2.840.113549.1.9.14"); // pkcs-9-at-extensionRequest

    if (seq != undefined) {
      var exts = pkijs.Extensions.fromBER(seq.values[0].toBER(false));
      logger.debug("exts:\n" + exts);
      for (var i = 0; i < exts.extensions.length; i++) {
        var ext = exts.extensions[i];
        if (ext.extnID === pkijs.id_SubjectAltName) {
          var octetString = asn1js.fromBER(ext.extnValue.toBER(false)).result;
          san = pkijs.GeneralNames.fromBER(octetString.getValue());
        }
      }
    }
  }

  // SANs in unser Format bringen
  var altnames = [];
  if (san != undefined) {
    san.names.forEach((element) => {
      var value = element.value;
      // IP-Adressen sind im Hex-Format und müssen umgewandelt werden
      // Es wird erwartet, dass IP-Adressen immer in voller Länge ankommen
      if (element.type === 7) {
        var ipAdr = value.valueBlock.valueHexView;
        if (ipAdr.byteLength === 4) {
          value = `${ipAdr[0]}.${ipAdr[1]}.${ipAdr[2]}.${ipAdr[3]}`;
        } else {
          // IPv6-Adressen müssen anders umgewandelt werden
          var tmp = pvtsutils.Convert.ToHex(value.getValue());
          var chunks = [];
          for (let i = 0; i < tmp.length; i += 4) {
            chunks.push(tmp.substring(i, i + 4));
          }
          value = chunks.join(":");
        }
      }
      altnames.push({
        type: getSanTypeById(element.type),
        value: value,
      });
    });
  }

  return { rdns: rdns, altnames: altnames };
}

/**
 * Konvertiert einen Hexstring in einen Dezimalstring.
 *
 * Brauchen wir um Seriennummern umzuwandeln. Wahrscheinlich bringt Forge auch
 * Werkzeuge mit, die das machen.
 *
 * @param {string} Hexstring
 * @returns {string} Dezimalstring
 */
export function hexToDec(s) {
  checkString("Eingabe in hexToDec", s);
  const hexPattern = /^[a-fA-F0-9]+$/;

  if (!s.match(hexPattern)) {
    throw new TypeError(
      "Eingabe in hexToDec ist kein Hexstring sondern: " + JSON.stringify(s)
    );
  }

  function add(xi, yi) {
    var c = 0;
    var r = [];
    var x = xi.split("").map(Number);
    var y = yi.split("").map(Number);
    while (x.length || y.length) {
      var s = (x.pop() || 0) + (y.pop() || 0) + c;
      r.unshift(s < 10 ? s : s - 10);
      c = s < 10 ? 0 : 1;
    }
    if (c) {
      r.unshift(c);
    }
    return r.join("");
  }

  var dec = "0";
  s.split("").forEach((chr) => {
    var n = parseInt(chr, 16);
    for (var t = 8; t; t >>= 1) {
      dec = add(dec, dec);
      if (n & t) {
        dec = add(dec, "1");
      }
    }
  });
  return dec;
}

/**
 * Parst einen DN wie Forge ihn verwendet und liefert ein DN-Objekt in unserem
 * Format.
 * TODO wird nicht mehr genutzt
 *
 * @param {Object} forgeDN DN aus einem Forge-Zertifikat Bsp.: forgeCert.subject
 * @returns {Object} DN im Wenja-Format Bsp.: {O: ["org"], CN: ["cn1", "cn2"]}
 */
export function getDN(forgeDN) {
  const dn = {};
  for (let attribute of forgeDN.attributes) {
    const type = getRdnTypeByOid(attribute.type).shortName;

    if (!Object.prototype.hasOwnProperty.call(dn, type)) {
      dn[type] = [];
    }

    dn[type].push(attribute.value);
  }
  return new Subject(dn);
}

/**
 * Parst ein Zertifikat im PEM-Format.
 *
 * @param {String} certPEM Zertifikat im PEM-Format
 * @returns {x509.X509Certificate} ein Zertifikat im PKIjs-Format
 */
export function _parseCert(certPEM) {
  const crypto = new Crypto();
  x509.cryptoProvider.set(crypto);
  checkPEM("certPEM in _parseCert", certPEM);
  const base64 = certPEM
    .replace(/-{5}(BEGIN|END) .*-{5}/gm, "")
    .replace(/\s/gm, "");
  const cert = new x509.X509Certificate(base64);
  return cert;
}

/**
 * Parst ein Zertifikat im PEM-Format.
 * Extrahiert alle für uns relevanten Informationen.
 *
 * @param {String} certPEM Zertifikat im PEM-Format
 * @returns {CertInfo} ein Objekt mit Zertifikatinformationen
 */
export function parseCert(certPEM) {
  const pkijsCert = _parseCert(certPEM);
  const extension = pkijsCert.getExtension(pkijs.id_SubjectAltName);
  const pkijsAltnames = extension !== null ? extension.names : null;
  const wenjaAltnames = pkijsAltnames !== null ? parseAltnames(pkijsAltnames) : [];

  const subject = parseOIDinDN(pkijsCert.subject); //TODO auch für issuer noch?
  logger.debug("parseCert Subject: " + subject);

  const certInfo = {
    subject: new Subject(getDNInWenjaFormatFromString(subject)),
    issuer: new Subject(getDNInWenjaFormatFromString(pkijsCert.issuer)),
    notBefore: pkijsCert.notBefore, //TODO ggf. hier noch .toDateString()
    notAfter: pkijsCert.notAfter,
    certSerial: hexToDec(pkijsCert.serialNumber),
    certPEM: certPEM,
    altnames: wenjaAltnames,
  };

  return certInfo;
}

/**
 * Erzeugt eine Stringdarstellung aus einer Forge-DN-Datenstruktur.
 * DNs sind in Forge ein Array aus Attributen (RDNs). Für die Umwandlung
 * wird das Array reduziert, indem Shortname (AttributeType) und value jedes
 * Eintrags mit Gleichheitszeichen verbunden und durch Komma abgeschlossen
 * werden.
 *
 * Als Eingabe wird ein Name erwartet, wie er beispielsweise in cert.issuer
 * steht:
 *
 * "issuer": {
 * "attributes": [
 *   {
 *     "type": "2.5.4.6",
 *     "value": "DE",
 *     "valueTagClass": 19,
 *     "name": "countryName",
 *     "shortName": "C"
 *  }, ... ], "hash": ... }
 * 
 * TODO wird nicht mehr genutzt
 *
 * @param {Object} name Name aus Forge-Datenstruktur (bspw. cert.issuer)
 * @returns {String} Stringdarstellung des Namens (bspw. C=DE, ...)
 */
export function nameToDN(name) {
  if (!name.attributes) {
    throw new Error("Übergebener Name hat keine attributes");
  }

  return name.attributes
    .reduce(
      (start, next) => start + next.shortName + "=" + next.value + ", ",
      ""
    )
    .slice(0, -2);
}

/**
 * Verschlüsselt einen Text. Kann mit __decrypt() unter Angabe des selben
 * Passworts wieder entschlüsselt werden. Es gilt die Invariante
 * __decrypt(__encrypt(plaintext, password), password) == plaintext.
 * Die Rückgabe ist eine Datenstruktur {encrypted, salt, iv} mit dem
 * verschlüsselten Text und dem verwendeten Salz und Initialisierungsvektor.
 * Alle Werte sind in Base64 kodiert.
 *
 * @param {string} plaintext der zu verschlüsselnde Text
 * @param {string} password Passwort zum Verschlüsseln
 * @returns {Data} verschlüsselte Daten {encrypted, salt, iv}
 */
function __encrypt(plaintext, password) {
  checkString("plaintext in encrypt", plaintext);
  checkString("password in encrypt", password);

  const iv = Buffer.from(nodecrypto.randomBytes(IV_LENGTH));
  const salt = Buffer.from(nodecrypto.randomBytes(SALT_LENGTH));
  const key = nodecrypto.pbkdf2Sync(
    password,
    salt,
    ITERATIONS,
    KEY_LENGTH,
    "sha1"
  );
  const textBuffer = Buffer.from(plaintext);

  const cipher = nodecrypto.createCipheriv(ENCRYPTION_ALGORITHM, key, iv);
  let encrypted = cipher.update(textBuffer, "utf-8", "base64");
  encrypted += cipher.final("base64");

  return {
    encrypted: encrypted,
    salt: Buffer.from(salt).toString("base64"),
    iv: Buffer.from(iv).toString("base64"),
  };
}

/**
 * Diese Methode wird nicht mehr verwendet. Sie dient nur als Information zur 
 * Funktion von __legacyDecrypt().
 * 
 * Verschlüsselt einen Text. Kann mit __legacyDecrypt() unter Angabe des selben
 * Passworts wieder entschlüsselt werden. Es gilt die Invariante
 * __legacyDecrypt(__legacyEncrypt(plaintext, password), password) == plaintext.
 * Die Rückgabe ist eine Datenstruktur {encrypted, salt, iv} mit dem
 * verschlüsselten Text und dem verwendeten Salz und Initialisierungsvektor.
 * Alle Werte sind in Base64 kodiert.
 *
 * @param {string} plaintext der zu verschlüsselnde Text
 * @param {string} password Passwort zum Verschlüsseln
 * @returns {Data} verschlüsselte Daten {encrypted, salt, iv}
 * @deprecated
 */
function __legacyEncrypt(plaintext, password) {
  checkString("plaintext in encrypt", plaintext);
  checkString("password in encrypt", password);
  logger.debug("legacyEncrypt");

  const iv = forge.random.getBytes(IV_LENGTH);
  const salt = forge.random.getBytesSync(SALT_LENGTH);
  const key = forge.pkcs5.pbkdf2(password, salt, ITERATIONS, KEY_LENGTH);

  var cipher = forge.cipher.createCipher(ENCRYPTION_MODE, key);
  cipher.start({ iv: iv });
  cipher.update(forge.util.createBuffer(plaintext));
  cipher.finish();
  var encrypted = cipher.output.getBytes();

  return {
    encrypted: forge.util.encode64(encrypted),
    salt: forge.util.encode64(salt),
    iv: forge.util.encode64(iv),
  };
}

/**
 * Entschlüsselt eine mit __encrypt() verschlüsselte Nachricht. Die Eingabe
 * muss ein Output von __encrypt() und das verwendete Passwort sein.
 *
 * TODO: Nach der Migrationsphase zu Wenja 2.0 "export" entfernen.
 *
 * @param {string} encrypted verschlüsselte Nachricht als Base64-String
 * @param {string} salt beim Verschlüsseln verwendetes Salz als Base64-String
 * @param {string} iv beim Verschlüsseln verwendeter IV als Base64-String
 * @param {string} password Passwort zum Entschlüsseln
 * @returns {string} entschlüsselter Klartext
 */
export function __decrypt({ encrypted, salt, iv }, password) {
  checkString("encrypted in decrypt", encrypted);
  checkString("salt in decrypt", salt);
  checkString("iv in decrypt", iv);
  checkString("password in decrypt", password);

  try {
    const encryptedBin = Buffer.from(encrypted, "base64");
    const saltBin = Buffer.from(salt, "base64");
    const ivBin = Buffer.from(iv, "base64");
    const key = nodecrypto.pbkdf2Sync(
      password,
      saltBin,
      ITERATIONS,
      KEY_LENGTH,
      "sha1"
    );

    const decipher = nodecrypto.createDecipheriv(
      ENCRYPTION_ALGORITHM,
      key,
      ivBin
    );
    let decrypted = decipher.update(encryptedBin, "base64", "utf8");
    decrypted += decipher.final("utf8");

    return decrypted;
  } catch (e) {
    logger.error("Konnte Daten nicht entschlüsseln: " + e);
    throw new Error("Konnte Daten nicht entschlüsseln.", e);
  }
}

/**
 * Entschlüsselt eine mit der alten __encrypt(), jetzt __legacyEncrypt(), 
 * verschlüsselte Nachricht. Die Eingabe muss ein Output von __legacyEncrypt() 
 * und das verwendete Passwort sein.
 *
 * @param {string} encrypted verschlüsselte Nachricht als Base64-String
 * @param {string} salt beim Verschlüsseln verwendetes Salz als Base64-String
 * @param {string} iv beim Verschlüsseln verwendeter IV als Base64-String
 * @param {string} password Passwort zum Entschlüsseln
 * @returns {string} entschlüsselter Klartext
 * @deprecated
 */
function __legacyDecrypt({ encrypted, salt, iv } , password) {
  checkString("encrypted in decrypt", encrypted);
  checkString("salt in decrypt", salt);
  checkString("iv in decrypt", iv);
  checkString("password in decrypt", password);
  logger.debug("legacyDecrypt");

  try {
    const encryptedBin = forge.util.decode64(encrypted);
    const saltBin = forge.util.decode64(salt);
    const ivBin = forge.util.decode64(iv);

    const key = forge.pkcs5.pbkdf2(password, saltBin, ITERATIONS, KEY_LENGTH);
    var decipher = forge.cipher.createDecipher(ENCRYPTION_MODE, key);
    decipher.start({ iv: ivBin });
    decipher.update(forge.util.createBuffer(encryptedBin));
    decipher.finish();

    return decipher.output.toString();
  } catch (e) {
    logger.error("Konnte Daten nicht entschlüsseln: " + e);
    throw new Error("Konnte Daten nicht entschlüsseln.", e);
  }

}

/**
 * Verschlüsselt ein beliebiges Objekt. Kann mit decrypt() unter Angabe des
 * selben Passworts wieder entschlüsselt werden. Es gilt die Invariante
 * decrypt(encrypt(plainData, password), password) == plainData.
 * Die Rückgabe ist eine Datenstruktur {encrypted, salt, iv} mit dem
 * verschlüsselten Objekt und dem verwendeten Salz und Initialisierungsvektor.
 * Alle Werte sind in Base64 kodiert.
 *
 * @param {any} plainData die zu verschlüsselnden Daten
 * @param {string} password Passwort zum Verschlüsseln
 * @returns {Data} verschlüsselte Daten {encrypted, salt, iv}
 */
export function encrypt(plainData, password) {
  const data = { plainData: plainData, knownPlainText: "knownPlainText" };
  return __encrypt(JSON.stringify(data), password);
}

/**
 * Funktioniert wie die normale encrypt-Methode aber arbeitet mit node-forge
 * statt pkijs. Zum Aufruf dieser Methode sollte es nicht mehr kommen.
 * 
 * @param {any} plainData die zu verschlüsselnden Daten
 * @param {string} password Passwort zum Verschlüsseln
 * @returns {Data} verschlüsselte Daten {encrypted, salt, iv}
 * @deprecated
 */
export function legacyEncrypt(plainData, password) {
  const data = { plainData: plainData, knownPlainText: "knownPlainText" };
  return __legacyEncrypt(JSON.stringify(data), password);
}

/**
 * Entschlüsselt eine mit encrypt() verschlüsselte Nachricht. Die Eingabe
 * muss ein Output von encrypt() und das verwendete Passwort sein.
 *
 * @param {string} encrypted verschlüsselte Nachricht als Base64-String
 * @param {string} salt beim Verschlüsseln verwendetes Salz als Base64-String
 * @param {string} iv beim Verschlüsseln verwendeter IV als Base64-String
 * @param {string} password Passwort zum Entschlüsseln
 * @returns {any} entschlüsselte Daten
 */
export function decrypt({ encrypted, salt, iv }, password) {
  const data = JSON.parse(__decrypt({ encrypted, salt, iv }, password));

  if (!data.knownPlainText || data.knownPlainText != "knownPlainText") {
    throw new Error("Konnte Daten nicht entschlüsseln.");
  }

  return data.plainData;
}

/**
 * Funktioniert wie die normale decrypt-Methode aber arbeitet mit node-forge
 * statt pkijs. Die Eingabe muss ein Output von legacyEncrypt() und das 
 * verwendete Passwort sein.
 *
 * @param {string} encrypted verschlüsselte Nachricht als Base64-String
 * @param {string} salt beim Verschlüsseln verwendetes Salz als Base64-String
 * @param {string} iv beim Verschlüsseln verwendeter IV als Base64-String
 * @param {string} password Passwort zum Entschlüsseln
 * @returns {any} entschlüsselte Daten
 * @deprecated
 */
export function legacyDecrypt({ encrypted, salt, iv }, password) {
  const data = JSON.parse(__legacyDecrypt({ encrypted, salt, iv }, password));

  if (!data.knownPlainText || data.knownPlainText != "knownPlainText") {
    throw new Error("Konnte Daten nicht entschlüsseln.");
  }

  return data.plainData;
}

/** Verschlüsselt einen privaten Schlüssel, der als Plain-PEM vorliegt
 *   als Encrypted-PEM
 *
 * @param {*} plainKey unverschlüsselter Private-Key als PEM
 * @param {*} password Paswort zum Verschlüsseln
 * @returns verschlüsselten Private-Key als PEM
 */
export async function encryptPrivateKeyToPem(plainKey, password) {
  const prevEngine = pkijs.getEngine();
  const nodeEngine = new NodeEngine();
  pkijs.setEngine(nodeEngine.name, nodeEngine);
  const passwordConverted = pvtsutils.Convert.FromString(password);
  const pkcs8Simpl = toPrivateKeyInfo(plainKey, password)
  const pkcs8Shrouded = new pkijs.PKCS8ShroudedKeyBag({parsedValue: pkcs8Simpl});
  const contentEncryptionAlgorithm= pkijs.getAlgorithmParameters(
    ENCRYPTION_ALGORITHM, 
    "encrypt"
  ).algorithm;

  if ("name" in contentEncryptionAlgorithm === false)
    throw new Error(`No support for selected algorithm: ${ENCRYPTION_ALGORITHM}`);

  await pkcs8Shrouded.makeInternalValues({
    password: passwordConverted,
    iterationCount: 100000,    
    hmacHashAlgorithm: "SHA-1", // OpenSSL can handle SHA-1 only
    contentEncryptionAlgorithm
  });

  const result = toPEM(pkcs8Shrouded.toSchema().toBER(false), "ENCRYPTED PRIVATE KEY");
  pkijs.setEngine(prevEngine);
  return result;
}

/** Verschlüsselt einen RSA-Schlüssel, der als Plain-PEM vorliegt
 *   als Encrypted-PEM
 *
 * @param {*} plainKey unverschlüsselter RSA-Private-Key als PEM
 * @param {*} password Paswort zum Verschlüsseln
 * @returns
 */
export function legacyEncryptRSAKeyToPem(plainKey, password) {
  var privKey = forge.pki.privateKeyFromPem(plainKey);
  return forge.pki.encryptRsaPrivateKey(privKey, password);
}

function toPrivateKeyInfo(plainKey, password) {
  try {
    return pkijs.PrivateKeyInfo.fromBER(fromPEM(plainKey));
  } catch (e) {
    const forgePrivateKey = forge.pki.privateKeyFromPem(plainKey);
    const privateKeyAsn1 = forge.pki.privateKeyToAsn1(forgePrivateKey);
    const privateKeyDer = forge.asn1.toDer(privateKeyAsn1);
    const derBuffer = privateKeyDer.getBytes();
    const arrayBuffer = new Uint8Array(derBuffer.length);
    for (let i = 0; i < derBuffer.length; i++) {
        arrayBuffer[i] = derBuffer.charCodeAt(i);
    }
    const asn1 = asn1js.fromBER(arrayBuffer.buffer);
    if (asn1.offset === -1) {
        throw new Error('ASN.1 parsing failed');
    }
    const privateKeyInfo = new pkijs.PrivateKeyInfo();
    privateKeyInfo.fromJSON({
        algorithm: {
            algorithmId: '1.2.840.113549.1.1.1', // rsaEncryption OID
        },
    });
    privateKeyInfo.privateKey = new asn1js.OctetString({ valueHex: arrayBuffer.buffer });
    return privateKeyInfo;
  }
}